import React, { useEffect, useState, useRef } from "react";
import ApiEnterpriseName from "./api_hits/ApiEnterpriseName";
import { getAuthHeader } from "../../../utils/getAuthHeader";

const API_BASE_FARMS = process.env.REACT_APP_API_URL;
const API_BASE_ADMIN = process.env.REACT_APP_API_URL_2;
//const TOKEN = localStorage.getItem("access_token") || "";

// ── Types ──────────────────────────────────────────────────────────────────────

interface FarmRow {
  enterprise_id: number;
  farm_id: string;
  farmer_name: string;
  is_paid: boolean;
  s6_status: "active" | "inactive";
  start_date: string;
  end_date: string | null;
}

interface PaginationMeta {
  total_count: number;
  total_pages: number;
  current_page: number;
  limit: number;
}

interface FailedFarm {
  farm_id: string;
  reason: string;
  message: string;
  extra_data?: Record<string, unknown>;
  activated_by_email: string;
  activated_by_name: string;
}

interface ActivationResult {
  message: string;
  status_code: number;
  data: {
    total_farms: number;
    successful: number;
    failed: number;
    successful_farm_ids: string[];
    failed_farms: FailedFarm[];
  };
  timestamp: string;
  request_id: string | number;
}

type SearchBy = "farm_id" | "farmer_name";

// ── Component ──────────────────────────────────────────────────────────────────

export default function S6ActivationDashboard() {
  const [selectedEnterprise, setSelectedEnterprise] = useState<number | null>(null);
  const [farms, setFarms] = useState<FarmRow[]>([]);
  const [selectedFarms, setSelectedFarms] = useState<FarmRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);

  const [activationResult, setActivationResult] = useState<ActivationResult | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);

  // 👈 Store admin api key received from s6-activation response — never shown in UI
  const adminApiKeyRef = useRef<string | null>(null);

  const [pagination, setPagination] = useState<PaginationMeta>({
    total_count: 0,
    total_pages: 1,
    current_page: 1,
    limit: 10,
  });

  // ── Search State ──────────────────────────────────────────────────────────────
  const [searchBy, setSearchBy] = useState<SearchBy>("farm_id");
  const [searchValue, setSearchValue] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Fetch farms ───────────────────────────────────────────────────────────────

  const fetchFarms = async (
    enterpriseId: number,
    page = 1,
    search_by?: SearchBy,
    search_value?: string
  ) => {
    setLoading(true);
    try {
      let url = `${API_BASE_FARMS}/farm/s6-activation?company_id=${enterpriseId}&page=${page}&limit=${pagination.limit}`;

      if (search_value && search_value.trim() !== "") {
        url += `&search_by=${search_by}&search_value=${encodeURIComponent(search_value.trim())}`;
      }

      const res = await fetch(url, {
        headers: {
          ...getAuthHeader(),
        },
      });
      if (!res.ok) throw new Error(`API Error: ${res.status}`);
      const data = await res.json();
      setFarms(data.farms || []);
      setPagination({
        total_count: data.total_count,
        total_pages: data.total_pages,
        current_page: data.current_page,
        limit: data.limit,
      });

      //  Capture admin_api_key from response and store in ref (not in state — never rendered)
      if (data.api_key) {
        adminApiKeyRef.current = data.api_key;
        //console.log("[DEBUG] Admin API key received:", data.api_key); //  DEBUG LINE — remove before production
      }

    } catch (err) {
      console.error("Error fetching farms:", err);
      setFarms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedFarms([]);              // ← reset only on enterprise change
    adminApiKeyRef.current = null;

    if (selectedEnterprise) {
      fetchFarms(selectedEnterprise, 1);
    } else {
      setFarms([]);
    }
  }, [selectedEnterprise]);

  // ── Search handlers ───────────────────────────────────────────────────────────

  const handleSearchByChange = (newSearchBy: SearchBy) => {
    setSearchBy(newSearchBy);
    setSearchValue("");
    if (selectedEnterprise) {
      fetchFarms(selectedEnterprise, 1, newSearchBy, "");
    }
  };

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    if (!selectedEnterprise) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchFarms(selectedEnterprise, 1, searchBy, value);
    }, 500);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    if (selectedEnterprise) {
      fetchFarms(selectedEnterprise, 1, searchBy, "");
    }
  };

  // ── Selection helpers ─────────────────────────────────────────────────────────

  const toggleFarm = (farm: FarmRow) => {
    setSelectedFarms((prev) =>
      prev.some((f) => f.farm_id === farm.farm_id)
        ? prev.filter((f) => f.farm_id !== farm.farm_id)
        : [...prev, farm]
    );
  };
  // Set lookup is O(1); array .includes is O(n) and called per row in render.
  const selectedSet = React.useMemo(
    () => new Set(selectedFarms.map((f) => f.farm_id)),
    [selectedFarms]
  );

  // How many of THIS PAGE's farms are currently selected.
  const visibleSelectedCount = farms.reduce(
    (n, f) => n + (selectedSet.has(f.farm_id) ? 1 : 0),
    0
  );

  const allSelected = farms.length > 0 && visibleSelectedCount === farms.length;
  const someSelected = visibleSelectedCount > 0 && !allSelected;

  // Render-time list:
  //  - All selected farms appear first (regardless of current page/filter)
  //  - Then any farms from the current page that aren't already selected
  //  - Deduped by farm_id so a selected farm that ALSO appears on the current page
  //    is only rendered once (at the top, as selected).
  const displayedFarms = React.useMemo(() => {
    const seen = new Set<string>();
    const result: FarmRow[] = [];

    // 1) selected first
    for (const f of selectedFarms) {
      if (!seen.has(f.farm_id)) {
        result.push(f);
        seen.add(f.farm_id);
      }
    }

    // 2) then unselected rows from the current page
    for (const f of farms) {
      if (!seen.has(f.farm_id)) {
        result.push(f);
        seen.add(f.farm_id);
      }
    }

    return result;
  }, [farms, selectedFarms]);

  const handleSelectAll = () => {
    if (allSelected) {
      // Unselect only this page's farms; preserve selections from other pages.
      const visibleIds = new Set(farms.map((f) => f.farm_id));
      setSelectedFarms((prev) => prev.filter((f) => !visibleIds.has(f.farm_id)));
    } else {
      // Add this page's farms (full row objects), dedup by farm_id.
      setSelectedFarms((prev) => {
        const existingIds = new Set(prev.map((f) => f.farm_id));
        const additions = farms.filter((f) => !existingIds.has(f.farm_id));
        return [...prev, ...additions];
      });
    }
  };

  const goToPage = (page: number) => {
    if (!selectedEnterprise) return;
    if (page < 1 || page > pagination.total_pages) return;
    fetchFarms(selectedEnterprise, page, searchBy, searchValue);
  };

  // ── Activation ────────────────────────────────────────────────────────────────

  const activateS6 = async () => {
    if (!selectedEnterprise || selectedFarms.length === 0) {
      alert("Please select at least one farm.");
      return;
    }

    // 👈 Guard — api key must be present before proceeding
    // Captured into a local so the value can't be nulled out mid-flight by an
    // enterprise change, and so it is non-null for the URL builder below.
    const apiKey = adminApiKeyRef.current;
    if (!apiKey) {
      alert("API key not available. Please re-select the enterprise and try again or the user is deleted from Crop Monitoring.");
      return;
    }

    // apihub's has_permission decorator resolves the caller from the endpoint's
    // query/path params only — it cannot see a key sent in the JSON body, and
    // now hard-fails with 404 USER_NOT_FOUND when it comes back empty. The key
    // stays in the body as well: apihub still reads body.api_key for request
    // logging and for the activated_by_* attribution on failed farms.
    const apiKeyQuery = `?api_key=${encodeURIComponent(apiKey)}`;

    setActivating(true);
    setActivationResult(null);
    setActivationError(null);

    try {
      const res = await fetch(`${API_BASE_ADMIN}/admin/s6-planetscope_subscription${apiKeyQuery}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        body: JSON.stringify({
          api_key: apiKey, // 👈 Use api key from ref, never from UI state
          farm_ids: selectedFarms.map((f) => f.farm_id),
        }),
      });

      const data: ActivationResult = await res.json();

      if (!res.ok) {
        setActivationError(data?.message || `Request failed with status ${res.status}`);
        setShowResultModal(true);
        return;
      }

      // 👈 If there are failed farms and a csv_report path exists, trigger download
      if (data.data.failed_farms && data.data.failed_farms.length > 0) {
        const csvRes = await fetch(
          `${API_BASE_ADMIN}/admin/s6-planetscope_subscription/download-failed-csv${apiKeyQuery}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeader(),
            },
            body: JSON.stringify({
              failed_farms: data.data.failed_farms.map((farm) => ({
                farm_id: farm.farm_id,
                reason: farm.reason,
                message: farm.message,
                extra_data: farm.extra_data || {},
                timestamp: data.timestamp,
                activated_by_email: farm.activated_by_email,
                activated_by_name: farm.activated_by_name,
              })),
            }),
          }
        );
        if (csvRes.ok) {
          const blob = await csvRes.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "failed_farms.csv";
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        }
      }

      setActivationResult(data);
      setShowResultModal(true);
      fetchFarms(selectedEnterprise, pagination.current_page, searchBy, searchValue);
      setSelectedFarms([]);

    } catch (err: any) {
      setActivationError(err?.message || "Unexpected error during activation.");
      setShowResultModal(true);
    } finally {
      setActivating(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const formatDate = (d: string | null) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const closeModal = () => {
    setShowResultModal(false);
    setActivationResult(null);
    setActivationError(null);
  };

  const buttonLabel = activating
    ? null
    : selectedFarms.length > 0
      ? `Activate S6 (${selectedFarms.length})`
      : "Activate S6";

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="container-fluid py-4">
      <h3 className="fw-bold mb-4">S6 Activation</h3>

      {/* ── Enterprise Selector ── */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <label className="fw-semibold mb-2 d-block">Select Enterprise</label>
          <ApiEnterpriseName
            selectedEnterprise={selectedEnterprise}
            setSelectedEnterprise={setSelectedEnterprise}
          />
        </div>
      </div>

      {/* ── Farm Table ── */}
      {selectedEnterprise && (
        <div className="card shadow-sm">

          {/* Card Header */}
          <div className="card-header d-flex justify-content-between align-items-center py-3">
            <span className="fw-bold">
              Farm List
              {pagination.total_count > 0 && (
                <span className="text-muted fw-normal ms-2" style={{ fontSize: "0.85rem" }}>
                  ({pagination.total_count} total)
                </span>
              )}
            </span>
            <div className="d-flex align-items-center gap-3">
              {selectedFarms.length > 0 && (
                <span className="badge bg-primary rounded-pill">
                  {selectedFarms.length} selected
                  {selectedFarms.length !== visibleSelectedCount && (
                    <> ({visibleSelectedCount} on this page)</>
                  )}
                </span>
              )}
              {selectedFarms.length > 0 && (
                <button
                  className="btn btn-sm btn-link text-danger p-0"
                  onClick={() => setSelectedFarms([])}
                  title="Clear selections across all pages"
                >
                  Clear all
                </button>
              )}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={handleSelectAll}
                disabled={farms.length === 0}
              >
                {allSelected ? "Unselect Page" : "Select Page"}
              </button>
            </div>
          </div>

          {/* ── Search Bar ── */}
          <div className="card-body border-bottom pb-3 pt-3">
            <div className="row g-2 align-items-center">
              <div className="col-auto">
                <label className="col-form-label col-form-label-sm fw-semibold text-muted">
                  Search By
                </label>
              </div>
              <div className="col-auto">
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: 140 }}
                  value={searchBy}
                  onChange={(e) => handleSearchByChange(e.target.value as SearchBy)}
                >
                  <option value="farm_id">Farm ID</option>
                  <option value="farmer_name">Farmer Name</option>
                </select>
              </div>
              <div className="col" style={{ maxWidth: 340 }}>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      fill="currentColor"
                      className="text-muted"
                      viewBox="0 0 16 16"
                    >
                      <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.099zm-5.242 1.156a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={
                      searchBy === "farm_id"
                        ? "Search by Farm ID..."
                        : "Search by Farmer Name..."
                    }
                    value={searchValue}
                    onChange={(e) => handleSearchValueChange(e.target.value)}
                  />
                  {searchValue && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={handleClearSearch}
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              {searchValue && (
                <div className="col-auto">
                  <span className="badge bg-info text-dark" style={{ fontSize: "0.75rem" }}>
                    Filtering by {searchBy === "farm_id" ? "Farm ID" : "Farmer Name"}:{" "}
                    <strong>{searchValue}</strong>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Table Body */}
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-2 text-muted">Loading farms...</p>
              </div>
            ) : displayedFarms.length === 0 ? (
              <div className="text-center py-5 text-muted">
                {searchValue
                  ? `No farms found matching "${searchValue}".`
                  : "No farms found for this enterprise."}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 48 }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected;
                          }}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Enterprise ID</th>
                      <th>Farm ID</th>
                      <th>Farmer Name</th>
                      <th>Is Paid</th>
                      <th>S6 Status</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedFarms.map((farm) => (
                      <tr
                        key={farm.farm_id}
                        onClick={() => toggleFarm(farm)}
                        style={{ cursor: "pointer" }}
                        className={
                          selectedSet.has(farm.farm_id) ? "table-primary" : ""
                        }
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedSet.has(farm.farm_id)}
                            onChange={() => toggleFarm(farm)}
                          />
                        </td>
                        <td className="text-muted">{farm.enterprise_id}</td>
                        <td>
                          <code style={{ fontSize: "0.8rem" }}>
                            {farm.farm_id.slice(0, 8)}...
                          </code>
                        </td>
                        <td className="fw-medium">{farm.farmer_name}</td>
                        <td>
                          <span
                            className={`badge ${farm.is_paid ? "bg-success" : "bg-secondary"
                              }`}
                          >
                            {farm.is_paid ? "Paid" : "Unpaid"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${farm.s6_status === "active"
                              ? "bg-success"
                              : "bg-danger bg-opacity-75"
                              }`}
                          >
                            {farm.s6_status === "active" ? "● Active" : "○ Inactive"}
                          </span>
                        </td>
                        <td>{formatDate(farm.start_date)}</td>
                        <td>{formatDate(farm.end_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Footer */}
          {!loading && pagination.total_pages > 1 && (
            <div className="card-footer d-flex justify-content-between align-items-center py-2">
              <small className="text-muted">
                Page {pagination.current_page} of {pagination.total_pages}
              </small>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${pagination.current_page === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => goToPage(pagination.current_page - 1)}>
                      &laquo;
                    </button>
                  </li>

                  {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === pagination.total_pages ||
                        Math.abs(p - pagination.current_page) <= 1
                    )
                    .reduce<(number | "...")[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === "..." ? (
                        <li key={`ellipsis-${idx}`} className="page-item disabled">
                          <span className="page-link">…</span>
                        </li>
                      ) : (
                        <li
                          key={p}
                          className={`page-item ${pagination.current_page === p ? "active" : ""}`}
                        >
                          <button className="page-link" onClick={() => goToPage(p as number)}>
                            {p}
                          </button>
                        </li>
                      )
                    )}

                  <li className={`page-item ${pagination.current_page === pagination.total_pages ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => goToPage(pagination.current_page + 1)}>
                      &raquo;
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      )}

      {/* ── Activate Button ── */}
      {selectedEnterprise && (
        <div className="mt-3 d-flex justify-content-end">
          <button
            className="btn btn-success px-4"
            disabled={activating || selectedFarms.length === 0}
            onClick={activateS6}
          >
            {activating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Activating...
              </>
            ) : (
              buttonLabel
            )}
          </button>
        </div>
      )}

      {/* ── Result Modal ── */}
      {showResultModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
            onClick={closeModal}
          />
          <div className="modal fade show d-block" style={{ zIndex: 1050 }} role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div
                  className={`modal-header ${activationError
                    ? "bg-danger text-white"
                    : activationResult?.data.failed === 0
                      ? "bg-success text-white"
                      : activationResult?.data.successful === 0
                        ? "bg-danger text-white"
                        : "bg-warning text-dark"
                    }`}
                >
                  <h5 className="modal-title fw-bold">
                    {activationError
                      ? "Activation Error"
                      : activationResult?.data.failed === 0
                        ? "All Farms Activated Successfully"
                        : activationResult?.data.successful === 0
                          ? "All Farms Failed"
                          : "Activation Partially Completed"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                    aria-label="Close"
                    style={
                      activationError ||
                        (activationResult && activationResult.data.successful === 0)
                        ? { filter: "invert(1)" }
                        : {}
                    }
                  />
                </div>

                <div className="modal-body">
                  {activationError && (
                    <div className="alert alert-danger mb-0">
                      <strong>Error:</strong> {activationError}
                      <p className="mb-0 mt-1 text-muted" style={{ fontSize: "0.82rem" }}>
                        No farms were processed. Please check your connection or contact support.
                      </p>
                    </div>
                  )}

                  {activationResult && (
                    <>
                      <div className="row g-3 mb-4">
                        <div className="col-4">
                          <div className="card text-center border-0 bg-light">
                            <div className="card-body py-3">
                              <div className="fw-bold text-secondary" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                                TOTAL SENT
                              </div>
                              <div className="fw-bold text-dark mt-1" style={{ fontSize: "1.8rem" }}>
                                {activationResult.data.total_farms}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="card text-center border-0 bg-success bg-opacity-10">
                            <div className="card-body py-3">
                              <div className="fw-bold text-success" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                                SUCCESSFUL
                              </div>
                              <div className="fw-bold text-success mt-1" style={{ fontSize: "1.8rem" }}>
                                {activationResult.data.successful}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="card text-center border-0 bg-danger bg-opacity-10">
                            <div className="card-body py-3">
                              <div className="fw-bold text-danger" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                                FAILED
                              </div>
                              <div className="fw-bold text-danger mt-1" style={{ fontSize: "1.8rem" }}>
                                {activationResult.data.failed}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 👈 CSV auto-downloads silently — no path shown in UI */}
                      {activationResult.data.failed_farms.length > 0 && (
                        <div className="alert alert-info d-flex align-items-center gap-2 py-2 mb-4">
                          <span style={{ fontSize: "1.1rem" }}>📄</span>
                          <span style={{ fontSize: "0.85rem" }}>
                            Failed farms CSV has been <strong>downloaded automatically</strong>.
                          </span>
                        </div>
                      )}

                      {activationResult.data.failed_farms.length > 0 && (
                        <>
                          <h6 className="fw-bold mb-2 text-danger">
                            Failed Farms ({activationResult.data.failed_farms.length})
                          </h6>
                          <div className="table-responsive" style={{ maxHeight: 280, overflowY: "auto" }}>
                            <table className="table table-sm table-bordered align-middle mb-0">
                              <thead className="table-danger">
                                <tr>
                                  <th style={{ width: 40 }}>#</th>
                                  <th>Farm ID</th>
                                  <th>Reason Code</th>
                                  <th>Message</th>
                                </tr>
                              </thead>
                              <tbody>
                                {activationResult.data.failed_farms.map((f, idx) => (
                                  <tr key={f.farm_id}>
                                    <td className="text-muted">{idx + 1}</td>
                                    <td>
                                      <code style={{ fontSize: "0.78rem" }}>
                                        {f.farm_id.slice(0, 8)}...
                                      </code>
                                    </td>
                                    <td>
                                      <span className="badge bg-danger bg-opacity-75 text-white">
                                        {f.reason}
                                      </span>
                                    </td>
                                    <td style={{ fontSize: "0.82rem" }}>{f.message}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}

                      {activationResult.data.successful_farm_ids.length > 0 && (
                        <details className="mt-3">
                          <summary className="fw-semibold text-success" style={{ cursor: "pointer", fontSize: "0.9rem" }}>
                            View successfully activated farm IDs ({activationResult.data.successful_farm_ids.length})
                          </summary>
                          <div className="mt-2 p-2 bg-light rounded" style={{ maxHeight: 160, overflowY: "auto", fontSize: "0.78rem" }}>
                            {activationResult.data.successful_farm_ids.map((id) => (
                              <code key={id} className="d-block text-success">{id}</code>
                            ))}
                          </div>
                        </details>
                      )}

                      <p className="text-muted mt-3 mb-0" style={{ fontSize: "0.75rem" }}>
                        Request ID: <code>{activationResult.request_id}</code>
                        &nbsp;|&nbsp;
                        {new Date(activationResult.timestamp).toLocaleString()}
                      </p>
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}